import { ee } from '../../services/earth-engine';
import { mergeProperties, retrieveExtremes } from '../utils';
import * as Metadata from '../../common/metadata';

/**
 * @function sliceByRevisit
 * @description Filters an image collection by a time period, either by minutes (for exact timestamps) or by days (from start of day).
 * @param {ee.ImageCollection} collection - Earth Engine image collection to filter
 * @param {String|ee.Date} startingDate - Starting date for the filter (ISO string or ee.Date)
 * @param {number} [days=0] - Number of days to advance from starting date (used when minutes is 0)
 * @param {number} [minutes=0] - Number of minutes to advance from starting date (overrides days if > 0)
 * * Called By:
 * @see {acquireFromDate} Image Filter step - gets collection and apply mosaic
 * @returns {ee.ImageCollection} Filtered image collection within the specified time range
 */
const sliceByRevisit = (collection, startingDate, days = 0, minutes = 0) => {
  const start = ee.Date(startingDate);
  if (minutes > 0) {
    return collection.filterDate(start.advance(-1, 'minute'), start.advance(minutes, 'minute'));
  }
  const startOfDay = start.update(null, null, null, 0, 0, 0);
  return collection.filterDate(startOfDay, startOfDay.advance(days, 'day'));
};

export const acquireFromDate = (date, mission, geometry, isOneExactDate = false) => {
  const slice = sliceByRevisit(
    ee.ImageCollection(mission.name).filterBounds(geometry),
    date,
    isOneExactDate ? 0 : mission.cycle,
    isOneExactDate ? 1 : 0,
  );
  const mosaicked = slice.mosaic().clip(geometry);
  const image = mosaicked.set(mergeProperties(slice));
  return scoreClouds(image, geometry, mission.bands.qa);
};

export const maskLandsatImageMask = (img, mission) => {
  let ms = mission;
  const bandsFromMission = ms.bands;
  var ndwi_revise = img
    .select([bandsFromMission.green])
    .subtract(img.select([bandsFromMission.nir]))
    .divide(img.select([bandsFromMission.green]).add(img.select([bandsFromMission.nir])));
  img = img.updateMask(ndwi_revise.gt(0));
  return img;
};

export const maskLandsatCloudsRatio = (img, geometry, bandName) => {
  return scoreClouds(img, geometry, bandName);
};

export const scoreClouds = (image, geometry, qa, bitCloud, bitShadow) => {
  // const cloud = "((b(0) >> 6) & 1)"; //C1
  const cloud = '((b(0) >> 3) & 1)'; //C2

  //const shadow = "((b(0) >> 3) & 1)"; //C1
  const shadow = '((b(0) >> 4) & 1)'; //C2

  //const confidence = "((b(0) >> 8) & 1) && ((b(0) >> 9) & 1)"; //C1
  //const confidence = "((b(0) >> 8) & 3) && ((b(0) >> 9) & 3)"; //C2
  //const confidence = "((b(0) >> 8) & 3) && ((b(0) >> 9) & 3)  && ((b(0) >> 10) & 3)  && ((b(0) >> 11) & 3)"; //C2

  //const expr = `(${confidence} > 0) && (${cloud} || ${shadow})`;
  const expr = `(${cloud} || ${shadow})`;
  const filtered = image.select(qa).expression(expr);

  console.log('QA', qa);
  const imageArea = filtered.multiply(ee.Image.pixelArea());
  console.log('ImageArea', imageArea);
  const res = imageArea.reduceRegion({
    reducer: ee.Reducer.sum(),
    scale: 1000,
    maxPixels: 1e9,
    geometry: geometry,
  });
  console.log('res', res);
  const cloudyArea = res.get(qa);
  const ratio = ee.Number(cloudyArea).divide(geometry.area(1));
  console.log('Cloudy area:', cloudyArea, 'Total area:', geometry.area(1), 'Cloud coverage ratio:', ratio);
  const timestamp = ee.Number(Date.now());
  return image.set('CLOUDS', ratio).set('CALC_TIME', timestamp);
};

// Computes a list of valid dates in the region to be retrieved with acquireFromDate.
// startDate/endDate (optional) scope the cycle-windowing to a sub-range instead
// of the mission's entire history, so callers can split a long history into
// smaller, parallelizable chunks (see acquisition/saga.js).
export const processCollection = (mission, geometry, startDate, endDate) => {
  let query = ee.ImageCollection(mission.name).filterBounds(geometry);
  if (startDate || endDate) {
    query = query.filterDate(startDate || `${mission.startYear}-01-01`, endDate || ee.Date(Date.now()));
  }

  // This intentionally skips the old "northeastern tile" phase-alignment step
  // (sorting by grid position, re-deriving extremes on that subset, etc.) —
  // it never affected which real dates get returned (the cycle windows below
  // still partition the whole query span with no gaps regardless of phase),
  // it only shifted the cosmetic anchor date, and its extra sub-collection
  // filters were a recurring source of "empty collection" crashes on the
  // smaller, sparser per-year chunks used here (see acquisition/saga.js).
  const process = (available) => {
    // Retrieve the globally extreme dates (earliest and latest)
    const global = retrieveExtremes(available);
    const earliestDate = global.earliest;

    // Compute the amount of complete orbital cycles between the earliest and
    // latest dates in the collection. .max(1): a chunk whose images all fall
    // on the same day (or a single image) makes this 0, and ee.List.sequence
    // with a 0 count/length is not a valid empty-sequence request for this EE
    // algorithm — it throws instead of just yielding an empty list, so always
    // ask for at least one.
    const completeCycles = ee
      .Number(global.latest.difference(earliestDate, 'day').abs())
      .divide(mission.cycle)
      .ceil()
      .max(1);

    // Generate slices of 16 (0, 16, 32, 48, ...), one for each complete cycle
    const additions = ee.List.sequence(0, null, mission.cycle, completeCycles);

    // Transform each slice into an empty image. If the slice contains at least
    // one image, we carry over its metadata (including the scene's cloud
    // percentage) to allow for filtering and reading cloud cover later, without
    // touching pixel data (no per-date mosaic/clip/reduceRegion)
    const carriers = additions.map((increment) => {
      const startingDate = earliestDate.advance(increment, 'day');
      const collection = sliceByRevisit(available, startingDate, mission.cycle);

      const empty = ee.Algorithms.IsEqual(collection.size(), 0);
      const firstImage = ee.Image(collection.sort(Metadata.TIME_START).first());
      const cloudCoverage = ee.Algorithms.If(
        empty,
        0,
        ee.Algorithms.If(firstImage.propertyNames().contains('CLOUD_COVER'), firstImage.get('CLOUD_COVER'), 0),
      );
      const properties = ee.Algorithms.If(empty, {}, mergeProperties(collection));

      return ee.Image(0).set(properties).set('CLOUD_COVER', cloudCoverage);
    });

    // Keep only the images whose combined geometries contain the AOI
    return ee.ImageCollection.fromImages(carriers).filter(ee.Filter.contains(Metadata.FOOTPRINT, geometry));
  };

  return ee.ImageCollection(ee.Algorithms.If(query.size().gt(0), process(query), ee.ImageCollection([])));
};

export const generateCloudMap = (collection) => {
  const dates = collection.aggregate_array(Metadata.TIME_START).map((d) => ee.Date(d).format(null, 'UTC'));
  const clouds = collection.aggregate_array('CLOUD_COVER');

  return ee.Dictionary.fromLists(dates, clouds);
};

// startDate/endDate (optional) scope the cycle-windowing to a sub-range instead
// of the mission's entire history, so callers can split a long history into
// smaller, parallelizable chunks (see acquisition/saga.js).
const queryAvailable = (mission) => (geometry, startDate, endDate) => {
  const collection = processCollection(mission, geometry, startDate, endDate);
  return generateCloudMap(collection);
};

const getAvailable = (mission) => (geometry) => {};

const acquire = (mission) => (date, geometry) => {
  return acquireFromDate(date, mission, geometry, true);
};

const format = (properties) => {
  return properties['system:time_start'] + ' -- ' + properties['cloud'];
};

export const fillGaps = (image) => {
  const spacecraft = ee.String(image.get('SPACECRAFT_ID'));

  const filled1 = image.focal_mean(1, 'square', 'pixels', 5);
  const filled2 = image.focal_mean(2, 'square', 'pixels', 5);
  const filled3 = image.focal_mean(3, 'square', 'pixels', 5);

  const filled = image.unmask(filled1).unmask(filled2).unmask(filled3);

  return ee.Image(ee.Algorithms.If(spacecraft.equals('LANDSAT_7'), filled, image));
};

export const reflectanceCorrectionLandsat = (image) => {
  const opticalBands = image.select('SR_B.*').multiply(0.0000275).add(-0.2);
  return image.addBands(opticalBands, null, true);
};

export const getBandMappingForSatellite = (image) => {
  const spacecraft = ee.String(image.get('SPACECRAFT_ID'));

  return ee.Dictionary(
    ee.Algorithms.If(
      spacecraft.equals('LANDSAT_5'),
      { BLUE: 'SR_B1', GREEN: 'SR_B2', RED: 'SR_B3', NIR: 'SR_B4', SWIR1: 'SR_B5', SWIR2: 'SR_B7' },
      ee.Algorithms.If(
        spacecraft.equals('LANDSAT_7'),
        { BLUE: 'SR_B1', GREEN: 'SR_B2', RED: 'SR_B3', NIR: 'SR_B4', SWIR1: 'SR_B5', SWIR2: 'SR_B7' },
        // LANDSAT_8 e LANDSAT_9
        { BLUE: 'SR_B2', GREEN: 'SR_B3', RED: 'SR_B4', NIR: 'SR_B5', SWIR1: 'SR_B6', SWIR2: 'SR_B7' },
      ),
    ),
  );
};

/**
 * @function calculateCloudCoverage
 * @description Calculates cloud percentage for Landsat L2 (Surface Reflectance) images.
 * Uses QA_PIXEL band to detect clouds based on bit flags.
 * @param {Object} satellite - Satellite object containing missions array
 * @param {ee.Geometry} geometry - Region of interest (ROI)
 * @param {String} startDate - Start date for filtering images
 * @returns {ee.ImageCollection} Collection with 'roiCloudPercentage' property
 * @throws {Error} Whatever went wrong, unchanged. It used to answer a failure with `ee.Number(0.01)`
 *  instead - a number where every caller expects a collection, so the next line to touch it died on
 *  `.filter is not a function` or `.features of undefined`, three frames from the real problem and
 *  carrying none of it.
 */
export const calculateCloudCoverage = function (satellite, geometry, startDate, includesLandsat7 = false) {
  const endDate = ee.Date(Date.now());
  const roi = geometry;
  const cloudConfidenceThresh = 2;
  console.log('Calculating cloud coverage for ROI (L2):', satellite.missions);
  try {
    // missions[0] = LT05, missions[1] = LC08, missions[2] = LC09, missions[3] = LE07
    const landsatLT05Collection = ee
      .ImageCollection(satellite.missions[0].name)
      .filterBounds(roi)
      .filterDate(startDate, endDate);
    const landsatLC08Collection = ee
      .ImageCollection(satellite.missions[1].name)
      .filterBounds(roi)
      .filterDate(startDate, endDate);
    const landsatLC09Collection = ee
      .ImageCollection(satellite.missions[2].name)
      .filterBounds(roi)
      .filterDate(startDate, endDate);
    const landsatLE07Collection = ee
      .ImageCollection(satellite.missions[3].name)
      .filterBounds(roi)
      .filterDate(startDate, endDate);
    const combinedCollection = landsatLT05Collection
      .merge(landsatLC08Collection)
      .merge(landsatLC09Collection)
      .merge(includesLandsat7 ? landsatLE07Collection : ee.ImageCollection([]));
    const roiCloudCollection = combinedCollection.map((image) => {
      const clippedImage = image.clip(roi);
      const qa = clippedImage.select('QA_PIXEL');
      const validDataMask = qa.mask();
      const validAreaImage = validDataMask.multiply(ee.Image.pixelArea());
      const validArea = validAreaImage
        .reduceRegion({
          reducer: ee.Reducer.sum(),
          geometry: roi,
          scale: 30,
          maxPixels: 1e9,
        })
        .get('QA_PIXEL');
      const cloudBitMask = 1 << 3;
      const cloudMask = qa.bitwiseAnd(cloudBitMask).neq(0);
      const cloudConfidenceBitMask = 3 << 8;
      const cloudConfidence = qa.bitwiseAnd(cloudConfidenceBitMask).rightShift(8);
      const likelyCloudMask = cloudMask.or(cloudConfidence.gte(cloudConfidenceThresh)).rename('CLOUDS');
      const sumCloudValuesInCloudArea = likelyCloudMask
        .reduceRegion({
          reducer: ee.Reducer.sum(),
          geometry: roi,
          scale: 30,
          maxPixels: 1e9,
          bestEffort: true,
        })
        .get('CLOUDS');
      const calculatedCloudPercentage = ee.Number(sumCloudValuesInCloudArea).divide(ee.Number(validArea));
      return image.set({ roiCloudPercentage: calculatedCloudPercentage });
    });

    return roiCloudCollection;
  } catch (e) {
    console.error('calculateCloudCoverage error:', e);
    throw e;
  }
};

export default {
  queryAvailable,
  getAvailable,
  acquire,
  format,
};
